// src/admin/pages/AdminPaymentDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Alert, Spinner, Badge, Button, Table } from 'react-bootstrap';
import { ArrowLeft, Printer, CheckCircle, CreditCard, User, Clock, Copy, Database, Calendar, Video } from 'lucide-react';

import { getPaymentLogDetails } from '../../services/adminApi'; 

import './AdminPaymentsLog.css';

const AdminPaymentDetails = () => {
    const { logId } = useParams();
    const navigate = useNavigate();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchDetails = async () => {
        try {
            const response = await getPaymentLogDetails(logId);
            setPayload(response.data.data);
        } catch (err) {
            setError('Failed to fetch transaction details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [logId]);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert('Copied to clipboard!');
    };

    const formatResponse = (response) => {
        if (!response) return 'No response payload available.';
        try {
            const parsed = typeof response === 'string' ? JSON.parse(response) : response;
            return JSON.stringify(parsed, null, 2);
        } catch (e) {
            return String(response);
        }
    };

    if (loading) return <div className="text-center p-5 admin-payments-container d-flex align-items-center justify-content-center"><Spinner animation="border" style={{ color: '#ffffff' }} /></div>;
    if (error) return <div className="container p-4 admin-payments-container"><Alert variant="danger">{error}</Alert></div>;
    const isSuccess = payload.payment_status === 'SUCCESS' || payload.payment_status === 'BOOKED';

    const handleViewUserProfile = () => {
        const targetUserId = payload.user?.id || payload.user?.userid || payload.user_id || payload.user?.uuid || payload.user?.joget_user_id;
        
        if (!targetUserId) {
            alert('User ID is not available for this transaction.');
            return;
        }

        const roleCode = String(payload.user?.role_code || payload.role_code || '');
        if (roleCode === '200') {
            navigate(`/admin/director-profile/${targetUserId}`);
        } else if (roleCode === '400') {
            navigate(`/admin/casting-profile/${targetUserId}`);
        } else {
            navigate(`/admin/candidate-profile/${targetUserId}?mode=view`);
        }
    };

    return (
        <div className="container-fluid py-4 admin-payments-container">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <h2 className="admin-payments-title">Transaction Details</h2>
                        <Badge bg={isSuccess ? 'success' : 'warning'} className="text-uppercase px-2 py-1">
                            {payload.payment_status}
                        </Badge>
                    </div>
                    <p className="admin-payments-subtitle">
                        Log reference ID: <span className="font-monospace fw-semibold text-white">#{payload.id}</span>
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Link to="/admin/payment-logs" className="btn btn-sm admin-payments-btn-secondary text-nowrap">
                        <ArrowLeft size={16} /> Back to List
                    </Link>
                    <Button variant="outline-secondary" className="btn-sm admin-payments-btn-secondary text-nowrap" onClick={() => window.print()}>
                        <Printer size={16} /> Print Receipt
                    </Button>
                    
                    {/* 🟢 सुधारी गई शर्त: यदि स्टेटस REFUNDED है, तो मैनुअल अप्रूवल का बटन अब यहाँ नहीं दिखाई देगा */}
                    {!isSuccess && payload.payment_status !== 'REFUNDED' && (
                        <Button className="btn-sm d-flex align-items-center gap-1 py-2 px-3 rounded-3 border-0 admin-payments-btn-danger text-nowrap" onClick={() => navigate(`/admin/payment-logs/${payload.id}/approve`)}>
                            <CheckCircle size={16} /> Manual Approval
                        </Button>
                    )}
                </div>
            </div>

            <Row className="g-4">
                {/* Left Side */}
                <Col lg={8}>
                    {/* Log Details Card */}
                    <Card className="border-0 rounded-4 mb-4 admin-payments-card">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 pb-3 mb-4 admin-payments-border-bottom">
                                <CreditCard size={20} className="text-danger" />
                                <h5 className="fw-bold m-0 text-white">Transaction Information</h5>
                            </div>
                            <Row>
                                <Col md={6}>
                                    <Table borderless className="mb-0 text-white">
                                        <tbody style={{ fontSize: '0.9rem' }}>
                                            <tr>
                                                <td className="admin-payments-table-text-muted py-2 ps-0">Status</td>
                                                <td><Badge bg={isSuccess ? 'success' : 'warning'}>{payload.payment_status}</Badge></td>
                                            </tr>
                                            <tr>
                                                <td className="admin-payments-table-text-muted py-2 ps-0">Booking Status</td>
                                                <td><Badge bg={payload.booking_status === 'Confirmed' ? 'primary' : 'secondary'}>{payload.booking_status}</Badge></td>
                                            </tr>
                                            <tr>
                                                <td className="admin-payments-table-text-muted py-2 ps-0">Order ID</td>
                                                <td className="font-monospace text-white">{payload.order_id || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <td className="admin-payments-table-text-muted py-2 ps-0">Payment ID</td>
                                                <td className="font-monospace text-white">{payload.payment_id || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <td className="admin-payments-table-text-muted py-2 ps-0">Amount</td>
                                                <td className="fw-bold text-white fs-5">₹{Number(payload.amount).toFixed(2)}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                                <Col md={6}>
                                    <Table borderless className="mb-0 text-white">
                                        <tbody style={{ fontSize: '0.9rem' }}>
                                            <tr>
                                                <td className="admin-payments-table-text-muted py-2 ps-0">Gateway Provider</td>
                                                <td><span className="badge admin-payments-badge-gateway">{payload.gateway_provider || 'N/A'}</span></td>
                                            </tr>
                                            <tr>
                                                <td className="admin-payments-table-text-muted py-2 ps-0">Payment Mode</td>
                                                <td className="text-white text-uppercase fw-semibold">{payload.payment_mode || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <td className="admin-payments-table-text-muted py-2 ps-0">Log Date & Time</td>
                                                <td className="admin-payments-table-text-muted">{new Date(payload.created_at).toLocaleString('en-IN')}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Booking Reference details */}
                    <Card className="border-0 rounded-4 mb-4 admin-payments-card">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 pb-3 mb-4 admin-payments-border-bottom">
                                <Calendar size={20} className="text-danger" />
                                <h5 className="fw-bold m-0 text-white">Slot & Audition Booking Info</h5>
                            </div>
                            <Table borderless className="mb-0 text-white" style={{ fontSize: '0.9rem' }}>
                                <tbody>
                                    <tr>
                                        <td className="admin-payments-table-text-muted py-2 ps-0" style={{ width: '30%' }}>Booking Type</td>
                                        <td className="fw-semibold text-white text-capitalize">{payload.booking_type || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="admin-payments-table-text-muted py-2 ps-0">Selected Slot (Datetime)</td>
                                        <td className="fw-semibold text-white">{payload.slot_datetime || 'N/A'}</td>
                                    </tr>
                                    {payload.meeting_link && (
                                        <tr>
                                            <td className="admin-payments-table-text-muted py-2 ps-0">
                                                <Video size={16} className="me-1 text-danger" /> Meeting Link
                                            </td>
                                            <td>
                                                <a href={payload.meeting_link} target="_blank" rel="noopener noreferrer" className="text-decoration-none font-monospace text-truncate d-inline-block text-danger" style={{ maxWidth: '400px' }}>
                                                    {payload.meeting_link}
                                                </a>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>

                    {/* Timeline */}
                    <Card className="border-0 rounded-4 mb-4 admin-payments-card">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 pb-3 mb-4 admin-payments-border-bottom">
                                <Clock size={20} className="text-danger" />
                                <h5 className="fw-bold m-0 text-white">Payment Log Timeline</h5>
                            </div>
                            <div className="admin-payments-timeline-container">
                                <div className="position-relative mb-4 pb-2 ps-3">
                                    <div className="admin-payments-timeline-dot-red">✓</div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="fw-bold text-white m-0">Payment Initialized</h6>
                                            <p className="admin-payments-table-text-muted small m-0">Order Generated: {payload.order_id || 'N/A'}</p>
                                        </div>
                                        <span className="admin-payments-table-text-muted small">{new Date(payload.created_at).toLocaleTimeString('en-IN')}</span>
                                    </div>
                                </div>
                                <div className="position-relative mb-2 pb-2 ps-3">
                                    <div className="admin-payments-timeline-dot-green">✓</div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="fw-bold text-white m-0">Transaction Handled</h6>
                                            <p className="admin-payments-table-text-muted small m-0">Gateway status updated to: <strong className="text-uppercase">{payload.payment_status}</strong></p>
                                        </div>
                                        <span className="admin-payments-table-text-muted small">{payload.payment_date ? new Date(payload.payment_date).toLocaleTimeString('en-IN') : '-'}</span>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Admin Override Remarks */}
                    {payload.modified_by_admin && (
                        <Card className="border-0 rounded-4 mb-4 admin-payments-card">
                            <Card.Body className="p-4">
                                <h5 className="fw-bold border-bottom pb-2 mb-3 text-white" style={{ borderBottomColor: '#2D3142' }}>Audit Remarks / Admin Action</h5>
                                <p className="text-white bg-dark p-3 rounded-3 border mb-0" style={{ fontSize: '0.9rem', borderColor: '#2D3142' }}>
                                    {payload.modified_by_admin}
                                </p>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Gateway Raw Payload Response */}
                    <Card className="border-0 rounded-4 admin-payments-card">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center justify-content-between pb-3 mb-4 admin-payments-border-bottom">
                                <div className="d-flex align-items-center gap-2">
                                    <Database size={20} className="text-danger" />
                                    <h5 className="fw-bold m-0 text-white">Gateway Payload Response</h5>
                                </div>
                                {payload.payment_response && (
                                    <Button variant="dark" size="sm" className="border d-flex align-items-center gap-1" onClick={() => copyToClipboard(payload.payment_response)} style={{ backgroundColor: '#1E1F29', borderColor: '#2D3142' }}>
                                        <Copy size={14} /> Copy Code
                                    </Button>
                                )}
                            </div>
                            <pre className="admin-payments-pre-box">
                                {formatResponse(payload.payment_response)}
                            </pre>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Side - User Info */}
                <Col lg={4}>
                    <Card className="border-0 rounded-4 mb-4 admin-payments-card">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 pb-3 mb-4 admin-payments-border-bottom">
                                <User size={20} className="text-danger" />
                                <h5 className="fw-bold m-0 text-white">User Information</h5>
                            </div>
                            <Table borderless className="mb-3 text-white" style={{ fontSize: '0.9rem' }}>
                                <tbody>
                                    <tr>
                                        <td className="admin-payments-table-text-muted py-1 ps-0">Name</td>
                                        <td className="fw-semibold text-white">{payload.user?.name || payload.user_name}</td>
                                    </tr>
                                    <tr>
                                        <td className="admin-payments-table-text-muted py-1 ps-0">Email</td>
                                        <td className="admin-payments-table-text-muted text-truncate" style={{ maxWidth: '180px' }}>{payload.user?.email || payload.email}</td>
                                    </tr>
                                    <tr>
                                        <td className="admin-payments-table-text-muted py-1 ps-0">Mobile</td>
                                        <td className="admin-payments-table-text-muted">{payload.user?.mobile || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="admin-payments-table-text-muted py-1 ps-0">User ID</td>
                                        <td className="admin-payments-table-text-muted font-monospace">{payload.user?.userid || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="admin-payments-table-text-muted py-1 ps-0">Registered On</td>
                                        <td className="admin-payments-table-text-muted">{payload.user?.created_at ? new Date(payload.user.created_at).toLocaleDateString('en-IN') : 'N/A'}</td>
                                    </tr>
                                </tbody>
                            </Table>
                            <Button 
                                variant="outline-danger" 
                                className="w-100 py-2 font-weight-bold admin-payments-btn-danger"
                                onClick={handleViewUserProfile}
                            >
                                View User Profile
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default AdminPaymentDetails;